/**
 * 파일 확장자 판별 및 아이콘 반환 유틸
 * ReferenceTag, AnswerSourceSideBar 등에서 사용
 */
import FileExtBadgeIcon from "@/components/ui/icons/FileExtBadgeIcon";


/** 배지를 가진 파일 계열. 색은 계열이 정하고 글자는 확장자가 정한다. */
type FileTypeKey = "pdf" | "word" | "excel" | "image";

const EXTENSION_GROUP: Record<string, FileTypeKey> = {
  pdf: "pdf",
  doc: "word",
  docx: "word",
  docs: "word",
  csv: "excel",
  xls: "excel",
  xlsx: "excel",
  png: "image",
  jpg: "image",
  jpeg: "image",
};

/* 배지 배경. svg fill 이라 클래스를 못 쓰고 값을 직접 쓴다 */
const BADGE_COLOR: Record<FileTypeKey, string> = {
  pdf: "#FF8187",
  word: "#5690FF",
  excel: "#0AC4B2",
  image: "#A880E0",
};

/** 배지 한 개분 정보. label 은 화면에 찍히는 글자라 합치는 기준이기도 하다 */
export interface FileBadgeInfo {
  key: FileTypeKey;
  label: string;
}

/** 제목의 확장자 → 배지 정보. 아는 확장자가 아니면 null */
export const getFileBadge = (title?: string): FileBadgeInfo | null => {
  const lower = (title ?? "").toLowerCase();
  /* 점이 없는 제목("PDF", "Docs")은 확장자가 아니라 웹 출처 이름이다 */
  const dot = lower.lastIndexOf(".");
  if (dot <= 0) return null;
  const ext = lower.slice(dot + 1);
  const key = EXTENSION_GROUP[ext];
  return key ? { key, label: ext.toUpperCase() } : null;
};

/** 배지를 가진 파일 확장자(pdf, doc(x), csv, xls(x), png, jpg 등)인지. 배지 규칙과 같은 기준을 쓴다 */
export const isFileTitle = (title: string) => getFileBadge(title) !== null;

/** 같은 배지를 하나로 합친다. 라벨이 다르면 다른 배지다(DOCX 와 DOC) */
export const dedupeBadges = (badges: FileBadgeInfo[]): FileBadgeInfo[] => {
  const seen = new Set<string>();
  return badges.filter((b) => !seen.has(b.label) && seen.add(b.label));
};

/** 파일 확장자 배지. Citation 패널과 출처 버튼이 같은 모양을 쓴다 */
export const FileTypeBadge = ({
  badge,
  height = 16,
  className,
}: {
  badge: FileBadgeInfo;
  height?: number;
  className?: string;
}) => (
  <FileExtBadgeIcon
    label={badge.label}
    color={BADGE_COLOR[badge.key]}
    height={height}
    className={className}
  />
);

/** 배지 여러 개를 겹쳐 보인다. 흰 링이 경계라 겹쳐도 구분된다 */
export const FileBadgeStack = ({
  badges,
  height = 16,
}: {
  badges: FileBadgeInfo[];
  height?: number;
}) => (
  <div className="flex items-center">
    {badges.map((badge, index) => (
      <span
        key={badge.label}
        className={`shrink-0 rounded-[4px] ring-2 ring-white ${index > 0 ? "-ml-[6px]" : ""}`}
      >
        <FileTypeBadge badge={badge} height={height} />
      </span>
    ))}
  </div>
);
